import React, { useState, useEffect } from 'react';
import { contractApi } from '../services/api';
import { ContractTemplate } from '../types';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import ConfirmModal from '../components/ConfirmModal';

const ContractTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [availableVariables, setAvailableVariables] = useState<Record<string, Record<string, string>>>({});
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: 'sale' as 'sale' | 'purchase',
    notes: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchTemplates();
    fetchAvailableVariables();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await contractApi.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      handleApiError(error, 'Hiba a sablonok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async () => {
    try {
      const variables = await contractApi.getAvailableVariables();
      setAvailableVariables(variables);
    } catch (error) {
      console.error('Failed to fetch variables:', error);
      handleApiError(error, 'Hiba a változók betöltésekor');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile && !editingTemplate) {
      showError('Kérlek válassz egy fájlt!');
      return;
    }

    try {
      if (editingTemplate) {
        // Update existing template (without file)
        await contractApi.updateTemplate(editingTemplate.id, formData);
        showSuccess('Sablon sikeresen frissítve');
      } else {
        // Create new template
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('template_type', formData.template_type);
        formDataToSend.append('notes', formData.notes);
        if (selectedFile) {
          formDataToSend.append('template_file', selectedFile);
        }

        await contractApi.createTemplate(formDataToSend);
        showSuccess('Sablon sikeresen feltöltve');
      }

      setShowModal(false);
      resetForm();
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to save template:', error);
      handleApiError(error, 'Hiba a sablon mentésekor');
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;

    try {
      await contractApi.deleteTemplate(deleteId);
      showSuccess('Sablon törölve');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      handleApiError(error, 'Hiba a sablon törlésekor');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownloadTemplate = async (template: ContractTemplate) => {
    try {
      const blob = await contractApi.downloadTemplate(template.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download template:', error);
      handleApiError(error, 'Hiba a sablon letöltésekor');
    }
  };

  const handleToggleActive = async (template: ContractTemplate) => {
    try {
      await contractApi.updateTemplate(template.id, { is_active: !template.is_active });
      showSuccess(template.is_active ? 'Sablon inaktiválva' : 'Sablon aktiválva');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to toggle template:', error);
      handleApiError(error, 'Hiba a sablon állapotának módosításakor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_type: 'sale',
      notes: '',
    });
    setSelectedFile(null);
    setEditingTemplate(null);
  };

  const openEditModal = (template: ContractTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      template_type: template.template_type,
      notes: template.notes || '',
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Szerződés Sablonok</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowVariablesModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Elérhető változók</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Új sablon</span>
          </button>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Név</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leírás</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fájl</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Státusz</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Még nincsenek sablonok feltöltve
                </td>
              </tr>
            ) : (
              templates.map((template) => (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      template.template_type === 'sale'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {template.template_type === 'sale' ? 'Eladás' : 'Vásárlás'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{template.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.original_filename}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        template.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template.is_active ? 'Aktív' : 'Inaktív'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDownloadTemplate(template)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Letöltés"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openEditModal(template)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      title="Szerkesztés"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setDeleteId(template.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Törlés"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Upload/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingTemplate ? 'Sablon szerkesztése' : 'Új sablon feltöltése'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Név *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Típus *</label>
                <select
                  value={formData.template_type}
                  onChange={(e) => setFormData({ ...formData, template_type: e.target.value as 'sale' | 'purchase' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!editingTemplate}
                >
                  <option value="sale">Eladás</option>
                  <option value="purchase">Vásárlás</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leírás</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {!editingTemplate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DOCX Sablonfájl *</label>
                  <input
                    type="file"
                    accept=".docx"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required={!editingTemplate}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Csak .docx fájlok, max 10MB. Használd az ${'{'}VÁLTOZÓ_NÉV{'}'} formátumot a helyettesítendő mezőkhöz.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Megjegyzések</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingTemplate ? 'Mentés' : 'Feltöltés'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Available Variables Modal */}
      {showVariablesModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Elérhető Változók</h2>
              <button onClick={() => setShowVariablesModal(false)} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                A Word sablonban használd az ${'{'}VÁLTOZÓ_NÉV{'}'} formátumot. A következő változók állnak rendelkezésre:
              </p>

              {Object.entries(availableVariables).map(([category, variables]) => (
                <div key={category} className="border rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-3 capitalize">{category.replace('_', ' ')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(variables).map(([key, description]) => (
                      <div key={key} className="bg-gray-50 p-2 rounded">
                        <code className="text-sm font-mono text-blue-600">${'{' + key + '}'}</code>
                        <p className="text-xs text-gray-600 mt-1">{description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">Állatlistás táblázat használata:</h4>
              <p className="text-sm text-yellow-700">
                Ha szeretnél állatlista táblázatot a szerződésbe, hozz létre egy táblázatot a Word-ben,
                és az első adatsorban használd a következő változókat: ANIMAL_EAR_TAG, ANIMAL_BREED,
                ANIMAL_GENDER, ANIMAL_WEIGHT, ANIMAL_PRICE_PER_KG, ANIMAL_TOTAL_PRICE stb.
                A rendszer automatikusan klónozza a sort minden állathoz.
              </p>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowVariablesModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteId !== null}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        title="Sablon törlése"
        message="Biztosan törölni szeretnéd ezt a sablont?"
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default ContractTemplates;
